/**
 * TripListPanel - Trip list with 3-state toggle for AutoPark Planning
 *
 * Displays trips with:
 * - 3-state cycling toggle: Unlocked → Locked → Excluded
 * - Current spot assignment
 * - Preview indicator (where it will move)
 * - Lock suggestions
 */

import BlockIcon from "@mui/icons-material/Block";
import ClearIcon from "@mui/icons-material/Clear";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  List,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useKeyboardNavigation } from "../hooks/useKeyboardNavigation";
import { GroupedTrip, ImpactSummary, PreviewMode, TripParkingState } from "../types";
import { formatTimeRange } from "../utils/timeFormatters";
import { AvailableLocation, ConflictInfo, MoveInfo, SizeBadge, TripListItem } from "./TripListItem";

interface TripListPanelProps {
  groupedTrips: GroupedTrip[];
  // 3-state toggle model
  tripStates: Map<string, TripParkingState>;
  onCycleTripState: (tripId: string) => void;
  // Batch actions
  onLockAll: () => void;
  onUnlockAll: () => void;
  onExcludeAll: () => void;
  onIncludeAll: () => void;
  // Preview
  impactSummary: ImpactSummary | null;
  previewMode: PreviewMode;
  // Hover callback for highlighting on Gantt timeline
  onTripHover?: (tripId: string | null) => void;
  // Show detailed developer info (size badges, etc.)
  showDetailedView?: boolean;
  // Assignment props (for unplaced aircraft)
  availableLocations?: AvailableLocation[];
  onAssignToLocation?: (
    tripId: string,
    locationId: string,
    locationType: "hangar" | "ramp",
    locationName: string
  ) => void;
}

export const TripListPanel: React.FC<TripListPanelProps> = ({
  groupedTrips,
  tripStates,
  onCycleTripState,
  onLockAll,
  onUnlockAll,
  onExcludeAll,
  onIncludeAll,
  impactSummary,
  previewMode,
  onTripHover,
  showDetailedView = false,
  availableLocations,
  onAssignToLocation,
}) => {
  const { t } = useTranslation();
  // Filter state for searching trips by tail/make/model
  const [filterText, setFilterText] = useState("");

  // Filter trips by tail number, make, or model
  const filteredTrips = useMemo(() => {
    if (!filterText.trim()) return groupedTrips;
    const lower = filterText.toLowerCase();
    return groupedTrips.filter(
      (trip) =>
        trip.tail_number?.toLowerCase().includes(lower) ||
        trip.aircraft_make?.toLowerCase().includes(lower) ||
        trip.aircraft_model?.toLowerCase().includes(lower)
    );
  }, [groupedTrips, filterText]);

  // Get trip IDs for keyboard navigation
  const tripIds = useMemo(() => filteredTrips.map((trip) => trip.trip_id), [filteredTrips]);

  // Use extracted keyboard navigation hook
  const { focusedIndex, listRef, containerRef, handleKeyDown } = useKeyboardNavigation(
    tripIds,
    { onCycleTripState, onLockAll, onUnlockAll, onExcludeAll, onIncludeAll },
    filterText
  );

  // Format time range using extracted utility (with i18n invalid message)
  const invalidDatesMessage = t("screens.ramp.autoPark.tripList.invalidDates");
  const formatTime = useCallback(
    (startTime: string, endTime: string) =>
      formatTimeRange(startTime, endTime, invalidDatesMessage),
    [invalidDatesMessage]
  );

  // Get move info for a trip from impact summary
  const getMoveInfo = useCallback(
    (tripId: string): MoveInfo | null => {
      if (!impactSummary || previewMode === "current") return null;
      return impactSummary.moves.find((m) => m.trip_id === tripId) || null;
    },
    [impactSummary, previewMode]
  );

  // Get conflict info for a trip
  const getConflictInfo = useCallback(
    (tripId: string): ConflictInfo | null => {
      if (!impactSummary || previewMode === "current") return null;
      return impactSummary.conflicts.find((c) => c.trip_id === tripId) || null;
    },
    [impactSummary, previewMode]
  );

  // Derive counts from tripStates
  const { selectedCount, lockedCount, excludedCount } = useMemo(() => {
    let selected = 0;
    let locked = 0;
    let excluded = 0;
    groupedTrips.forEach((trip) => {
      const state = tripStates.get(trip.trip_id) || "unlocked";
      if (state === "excluded") {
        excluded++;
      } else {
        selected++;
        if (state === "locked") {
          locked++;
        }
      }
    });
    return { selectedCount: selected, lockedCount: locked, excludedCount: excluded };
  }, [groupedTrips, tripStates]);
  const totalCount = groupedTrips.length;

  // Get size category badge color and label
  const getSizeBadge = useCallback(
    (trip: GroupedTrip): SizeBadge | null => {
      const wingspan = trip.wingspan;
      if (!wingspan) return null;

      // Size categories based on wingspan (feet)
      if (wingspan <= 50) {
        return {
          label: t("screens.ramp.autoPark.tripList.sizeCategories.small.label"),
          color: "#4CAF50",
          tooltip: t("screens.ramp.autoPark.tripList.sizeCategories.small.tooltip"),
        };
      } else if (wingspan <= 80) {
        return {
          label: t("screens.ramp.autoPark.tripList.sizeCategories.medium.label"),
          color: "#2196F3",
          tooltip: t("screens.ramp.autoPark.tripList.sizeCategories.medium.tooltip"),
        };
      } else if (wingspan <= 120) {
        return {
          label: t("screens.ramp.autoPark.tripList.sizeCategories.large.label"),
          color: "#FF9800",
          tooltip: t("screens.ramp.autoPark.tripList.sizeCategories.large.tooltip"),
        };
      } else {
        return {
          label: t("screens.ramp.autoPark.tripList.sizeCategories.extraLarge.label"),
          color: "#F44336",
          tooltip: t("screens.ramp.autoPark.tripList.sizeCategories.extraLarge.tooltip"),
        };
      }
    },
    [t]
  );

  return (
    <Box
      ref={containerRef}
      sx={{ display: "flex", flexDirection: "column", height: "100%" }}
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      {/* Header with batch actions */}
      <Box sx={{ p: 1.5, borderBottom: 1, borderColor: "divider" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
          <Tooltip title={t("screens.ramp.autoPark.tripList.tooltips.keyboardHint")}>
            <Typography variant="subtitle2" sx={{ cursor: "help" }}>
              {t("screens.ramp.autoPark.tripList.title", {
                selected: selectedCount,
                total: totalCount,
              })}
            </Typography>
          </Tooltip>
          <Stack direction="row" spacing={0.5}>
            <Tooltip title={t("screens.ramp.autoPark.tripList.tooltips.includeAll")}>
              <Button size="small" onClick={onIncludeAll}>
                {t("screens.ramp.autoPark.tripList.buttons.includeAll")}
              </Button>
            </Tooltip>
            <Tooltip title={t("screens.ramp.autoPark.tripList.tooltips.excludeAll")}>
              <Button size="small" onClick={onExcludeAll}>
                {t("screens.ramp.autoPark.tripList.buttons.excludeAll")}
              </Button>
            </Tooltip>
          </Stack>
        </Stack>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="caption" color="text.secondary">
            <LockIcon fontSize="inherit" sx={{ verticalAlign: "middle", mr: 0.5 }} />
            {lockedCount} locked
            {excludedCount > 0 && (
              <>
                <BlockIcon fontSize="inherit" sx={{ verticalAlign: "middle", ml: 1, mr: 0.5 }} />
                {excludedCount} excluded
              </>
            )}
          </Typography>
          <Stack direction="row" spacing={0.5}>
            <Tooltip title={t("screens.ramp.autoPark.tripList.tooltips.lockAll")}>
              <Button
                size="small"
                startIcon={<LockIcon fontSize="small" />}
                onClick={onLockAll}
              >
                {t("screens.ramp.autoPark.tripList.buttons.lockAll")}
              </Button>
            </Tooltip>
            <Tooltip title={t("screens.ramp.autoPark.tripList.tooltips.unlockAll")}>
              <Button
                size="small"
                startIcon={<LockOpenOutlinedIcon fontSize="small" />}
                onClick={onUnlockAll}
              >
                {t("screens.ramp.autoPark.tripList.buttons.unlockAll")}
              </Button>
            </Tooltip>
          </Stack>
        </Stack>

        {/* Search/Filter Input */}
        <TextField
          size="small"
          placeholder={t("screens.ramp.autoPark.tripList.filter.placeholder")}
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          fullWidth
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
              endAdornment: filterText && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setFilterText("")}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
          sx={{ mt: 1 }}
        />
      </Box>

      {/* Trip List */}
      <List
        ref={listRef}
        sx={{
          flex: 1,
          overflow: "auto",
          py: 0,
        }}
      >
        {filteredTrips.length === 0 ? (
          <Box
            sx={{
              p: 3,
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              {t("screens.ramp.autoPark.tripList.emptyState.title")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t("screens.ramp.autoPark.tripList.emptyState.hint")}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              {t("screens.ramp.autoPark.tripList.emptyState.addTrips")}
            </Typography>
          </Box>
        ) : (
          filteredTrips.map((trip, index) => {
            const tripState = tripStates.get(trip.trip_id) || "unlocked";
            return (
              <TripListItem
                key={trip.trip_id}
                trip={trip}
                tripState={tripState}
                index={index}
                isFocused={index === focusedIndex}
                moveInfo={getMoveInfo(trip.trip_id)}
                conflictInfo={getConflictInfo(trip.trip_id)}
                sizeBadge={getSizeBadge(trip)}
                formattedTimeRange={formatTime(trip.start_time, trip.end_time)}
                showDetailedView={showDetailedView}
                onCycleTripState={onCycleTripState}
                onTripHover={onTripHover}
                availableLocations={availableLocations}
                onAssignToLocation={onAssignToLocation}
              />
            );
          })
        )}
      </List>
    </Box>
  );
};
